/**
 * Landen voor offertes buiten de vaste verzendtabel.
 *
 * De webshop-checkout blijft beperkt tot de landen in FALLBACK_COUNTRY_RATES
 * (rates-fallback.ts). Via een admin-offerte mogen we bewust overal ter wereld
 * leveren; de admin vult dan zelf het verzendbedrag in. Dit bestand bepaalt
 * alleen welke landcodes geldig zijn en welk basis-btw-tarief daarbij hoort.
 * Het verzendtarief komt NOOIT uit een andere landrij (en zeker niet uit NL).
 *
 * Client-veilig: geen database-import, zodat ook de admin-UI en de checkout
 * dezelfde lijst gebruiken.
 */

import { isSupportedOrderCountry, type CountryRates } from './rates-fallback';
import { isEuVatCountry } from './vat';

/**
 * ISO 3166-1 alpha-2, officieel toegekende codes. Bewust een vaste lijst en
 * niet Intl.DisplayNames: die accepteert ook `UK`, `EU` en `EN`-achtige codes
 * afhankelijk van de ICU-versie in browser of Node.
 */
const ISO_3166_ALPHA2 = new Set([
  'AD', 'AE', 'AF', 'AG', 'AI', 'AL', 'AM', 'AO', 'AQ', 'AR', 'AS', 'AT', 'AU', 'AW', 'AX', 'AZ',
  'BA', 'BB', 'BD', 'BE', 'BF', 'BG', 'BH', 'BI', 'BJ', 'BL', 'BM', 'BN', 'BO', 'BQ', 'BR', 'BS',
  'BT', 'BV', 'BW', 'BY', 'BZ', 'CA', 'CC', 'CD', 'CF', 'CG', 'CH', 'CI', 'CK', 'CL', 'CM', 'CN',
  'CO', 'CR', 'CU', 'CV', 'CW', 'CX', 'CY', 'CZ', 'DE', 'DJ', 'DK', 'DM', 'DO', 'DZ', 'EC', 'EE',
  'EG', 'EH', 'ER', 'ES', 'ET', 'FI', 'FJ', 'FK', 'FM', 'FO', 'FR', 'GA', 'GB', 'GD', 'GE', 'GF',
  'GG', 'GH', 'GI', 'GL', 'GM', 'GN', 'GP', 'GQ', 'GR', 'GS', 'GT', 'GU', 'GW', 'GY', 'HK', 'HM',
  'HN', 'HR', 'HT', 'HU', 'ID', 'IE', 'IL', 'IM', 'IN', 'IO', 'IQ', 'IR', 'IS', 'IT', 'JE', 'JM',
  'JO', 'JP', 'KE', 'KG', 'KH', 'KI', 'KM', 'KN', 'KP', 'KR', 'KW', 'KY', 'KZ', 'LA', 'LB', 'LC',
  'LI', 'LK', 'LR', 'LS', 'LT', 'LU', 'LV', 'LY', 'MA', 'MC', 'MD', 'ME', 'MF', 'MG', 'MH', 'MK',
  'ML', 'MM', 'MN', 'MO', 'MP', 'MQ', 'MR', 'MS', 'MT', 'MU', 'MV', 'MW', 'MX', 'MY', 'MZ', 'NA',
  'NC', 'NE', 'NF', 'NG', 'NI', 'NL', 'NO', 'NP', 'NR', 'NU', 'NZ', 'OM', 'PA', 'PE', 'PF', 'PG',
  'PH', 'PK', 'PL', 'PM', 'PN', 'PR', 'PS', 'PT', 'PW', 'PY', 'QA', 'RE', 'RO', 'RS', 'RU', 'RW',
  'SA', 'SB', 'SC', 'SD', 'SE', 'SG', 'SH', 'SI', 'SJ', 'SK', 'SL', 'SM', 'SN', 'SO', 'SR', 'SS',
  'ST', 'SV', 'SX', 'SY', 'SZ', 'TC', 'TD', 'TF', 'TG', 'TH', 'TJ', 'TK', 'TL', 'TM', 'TN', 'TO',
  'TR', 'TT', 'TV', 'TW', 'TZ', 'UA', 'UG', 'UM', 'US', 'UY', 'UZ', 'VA', 'VC', 'VE', 'VG', 'VI',
  'VN', 'VU', 'WF', 'WS', 'YE', 'YT', 'ZA', 'ZM', 'ZW',
]);

export const ISO_COUNTRY_CODES: readonly string[] = Object.freeze([...ISO_3166_ALPHA2].sort());

export function normalizeIsoCountryCode(value: unknown): string | null {
  const code = String(value ?? '').trim().toUpperCase();
  return ISO_3166_ALPHA2.has(code) ? code : null;
}

export function isIsoCountryCode(value: unknown): boolean {
  return normalizeIsoCountryCode(value) !== null;
}

/**
 * Normale (standaard) btw-tarieven van de EU-lidstaten die NIET in de
 * verzendtabel staan. Nodig voor B2C-afstandsverkopen onder OSS: de klant
 * betaalt de btw van het leverland.
 *
 * Bron: Europese Commissie, Your Europe, "VAT rules and rates"
 * (europa.eu/youreurope/business/taxation/vat/vat-rules-rates), pagina
 * "last checked 13/07/2026", nagekeken op 12-10-2026.
 */
export const OSS_ONLY_EU_VAT_RATES: Readonly<Record<string, number>> = Object.freeze({
  PT: 23,
  GR: 24,
  FI: 25.5,
  RO: 21,
  BG: 20,
  CY: 19,
  MT: 18,
});

/**
 * Kan een admin-offerte naar dit land? Elk ISO-land, behalve EU-lidstaten
 * waarvan we het btw-tarief niet kennen (die bestaan na de lijst hierboven
 * niet meer, maar een nieuwe lidstaat moet expliciet worden toegevoegd).
 */
export function isQuoteDeliveryCountry(value: unknown): boolean {
  const code = normalizeIsoCountryCode(value);
  if (!code) return false;
  if (isSupportedOrderCountry(code)) return true;
  if (isEuVatCountry(code)) return code in OSS_ONLY_EU_VAT_RATES;
  return true;
}

/** Een offerte naar dit land heeft geen landtarief: de admin moet verzending invullen. */
export function requiresManualQuoteShipping(value: unknown): boolean {
  const code = normalizeIsoCountryCode(value);
  return Boolean(code && !isSupportedOrderCountry(code) && isQuoteDeliveryCountry(code));
}

export class UnsupportedQuoteCountryError extends Error {
  readonly code = 'UNSUPPORTED_COUNTRY' as const;
  constructor(readonly countryCode: string) {
    super(`Ongeldige of niet-ondersteunde landcode voor een offerte: ${countryCode || '(leeg)'}`);
    this.name = 'UnsupportedQuoteCountryError';
  }
}

/**
 * Basistarieven voor een offerteland buiten de verzendtabel. Buiten de EU:
 * 0% (uitvoer; resolveVatTreatment maakt er `export-outside-eu` van). Binnen
 * de EU: het tarief van het leverland (OSS), waarna resolveVatTreatment bij
 * een geldig btw-nummer alsnog verlegt. Verzending is altijd 0 hier: die
 * vult de admin per offerte met de hand in.
 */
export function quoteOnlyCountryRates(value: unknown): CountryRates {
  const code = normalizeIsoCountryCode(value);
  if (!code || isSupportedOrderCountry(code) || !isQuoteDeliveryCountry(code)) {
    throw new UnsupportedQuoteCountryError(String(value ?? '').trim().toUpperCase());
  }
  return {
    countryCode: code,
    vatRate: isEuVatCountry(code) ? OSS_ONLY_EU_VAT_RATES[code] : 0,
    shippingCost: 0,
    freeShippingFrom: 0,
    currency: 'EUR',
  };
}

/**
 * Het land waarmee leveranciersprijzen (Print.com, Probo, Gelato, ...) worden
 * opgevraagd. Die API's kennen alleen de landen uit de verzendtabel. Voor een
 * offerte naar een ander land rekenen we de productprijs (en de inkoopprijs)
 * uit alsof het naar Nederland gaat; de verzending naar het echte land zit in
 * het handmatige verzendbedrag van de offerte. Btw volgt altijd het echte
 * leverland, nooit dit land.
 */
export function supplierPricingCountry(value: unknown): string {
  const code = String(value ?? '').trim().toUpperCase();
  return isSupportedOrderCountry(code) ? code : 'NL';
}
